import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, MessageCircle, Phone, Mail } from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/format";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";

export function StatTile({ label, value, hint, accent, icon: Icon, to, testId }) {
  const content = (
    <Card className="rounded-3xl border-border/60 shadow-soft card-lift bg-card overflow-hidden" data-testid={testId}>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.06em] sm:tracking-[0.18em] font-bold text-muted-foreground whitespace-nowrap">{label}</p>
            <p className={`mt-2 sm:mt-3 font-display text-2xl sm:text-4xl font-black tracking-tight leading-none ${accent}`}>{value}</p>
          </div>
          {Icon && (
            <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-xl sm:rounded-2xl grid place-items-center shrink-0 ${
              accent === "text-red-600" ? "bg-red-50 text-red-600"
              : accent === "text-amber-600" ? "bg-amber-50 text-amber-600"
              : "bg-primary-50 text-primary-700"
            }`}>
              <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
          )}
        </div>
        {hint && <p className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">{hint}</p>}
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block tap">{content}</Link> : content;
}

export function FollowupRow({ fu, onComplete, onPing }) {
  const navigate = useNavigate();
  const pingedAgo = fu.client_pinged_at ? (() => {
    const min = Math.round((Date.now() - new Date(fu.client_pinged_at).getTime()) / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  })() : null;
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-2xl border border-border/60 bg-card hover:bg-muted/30 transition" data-testid={`fu-row-${fu.id}`}>
      <button
        onClick={() => onComplete(fu.id)}
        data-testid={`fu-complete-${fu.id}`}
        className="h-9 w-9 rounded-full border-2 border-border/80 hover:border-primary hover:bg-primary-50 grid place-items-center tap shrink-0 mt-0.5"
        aria-label="Mark complete"
      >
        <CheckCircle2 className="h-4 w-4 text-muted-foreground hover:text-primary" />
      </button>
      <div className="flex-1 min-w-0 space-y-1.5">
        {/* Tap title block to open the deal page when present — gives reps
            full context without needing a separate popup. */}
        <div
          role={fu.deal_id ? "button" : undefined}
          tabIndex={fu.deal_id ? 0 : undefined}
          onClick={() => fu.deal_id && navigate(`/deals/${fu.deal_id}`)}
          className={`flex items-start justify-between gap-2 ${fu.deal_id ? "cursor-pointer" : ""}`}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug break-words" data-testid={`home-fu-title-${fu.id}`}>{fu.title}</p>
            <p className="text-xs text-muted-foreground break-words mt-0.5">
              {fu.client_contact_person ? `${fu.client_contact_person} · ` : ""}
              {fu.client_name || "—"}
              {fu.deal_title ? ` · ${fu.deal_title}` : ""}
              {fu.deal_value ? ` · ${fmtINR(fu.deal_value)}` : ""}
            </p>
          </div>
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap shrink-0">{fmtDate(fu.due_date)}</span>
        </div>
        {fu.deal_stage && (
          <Badge variant="outline" className="text-[9px] uppercase tracking-wider px-1.5 py-0 capitalize" data-testid={`home-fu-stage-${fu.id}`}>
            {fu.deal_stage.replaceAll("_", " ")}
          </Badge>
        )}
        {/* Direct contact details — phone & email visible inline so reps can
            read the number without opening a separate dialog. */}
        {(fu.client_phone || fu.client_email) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {fu.client_phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{fu.client_phone}</span>}
            {fu.client_email && <span className="inline-flex items-center gap-1 break-all"><Mail className="h-3 w-3" />{fu.client_email}</span>}
          </div>
        )}
        {(fu.notes || fu.context) && (
          <p className="text-[11px] italic text-muted-foreground bg-muted/30 rounded-lg px-2.5 py-1.5 break-words" data-testid={`home-fu-notes-${fu.id}`}>
            "{fu.notes || fu.context}"
          </p>
        )}
        {(pingedAgo || fu.client_phone || fu.client_email) && (
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-1.5 lg:gap-2 pt-0.5">
            {pingedAgo ? (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wider inline-flex items-center gap-1 self-start shrink-0"
                data-testid={`home-fu-pinged-${fu.id}`}
              >
                <MessageCircle className="h-2.5 w-2.5" /> Pinged · {pingedAgo}
              </Badge>
            ) : <span className="hidden lg:inline" />}
            <div className="flex items-center gap-1.5 shrink-0 lg:ml-auto flex-wrap">
              {fu.client_phone && <CallButton phone={fu.client_phone} testId={`home-call-fu-${fu.id}`} />}
              {fu.client_phone && (
                <WhatsAppButton
                  phone={fu.client_phone}
                  email={fu.client_email}
                  contactName={fu.client_contact_person || fu.client_name}
                  dealTitle={fu.deal_title}
                  dealId={fu.deal_id}
                  clientId={fu.client_id}
                  clientCompany={fu.client_name}
                  action={fu.title}
                  defaultTemplate="follow_up_action"
                  followupId={fu.id}
                  label="Send"
                  onSent={onPing}
                  testId={`home-wa-fu-${fu.id}`}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function greetingForNow() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
